import React, { useState, useEffect, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Link2, Copy, Smartphone, AlertTriangle, Clock, Loader2, Route, Navigation, X, Satellite, RefreshCw, CheckCircle2, BatteryMedium } from "lucide-react";
import { trackLatest, trackHistory, trackWeather, trackLink, life360Status, life360Connect, life360Sync, life360Disconnect, gmapsStatus, gmapsConnect, gmapsSync, gmapsDisconnect } from "../lib/api";
import { toast } from "sonner";

const COLORS = ["#00F0FF", "#c084fc", "#10B981", "#F59E0B", "#EF4444", "#38bdf8", "#f472b6"];

function pinIcon(color, label) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #030712;box-shadow:0 2px 8px #000a;display:flex;align-items:center;justify-content:center;">
      <span style="transform:rotate(45deg);color:#030712;font-weight:800;font-size:12px;font-family:sans-serif;">${label}</span></div>`,
    iconSize: [30, 30], iconAnchor: [15, 28], popupAnchor: [0, -28],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points && points.length) {
      const b = L.latLngBounds(points);
      map.fitBounds(b, { padding: [50, 50], maxZoom: 15 });
    }
  }, [points, map]);
  return null;
}

function timeAgo(mins) {
  if (mins === null || mins === undefined) return "no location yet";
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.round(mins)} min ago`;
  const h = Math.floor(mins / 60);
  return `${h}h ${Math.round(mins % 60)}m ago`;
}

function SetupModal({ member, userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { setData(await trackLink(member.id, userId)); }
      catch (e) { toast.error("Could not create link"); }
      finally { setLoading(false); }
    })();
  }, [member.id, userId]);

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied"); };

  return (
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4" data-testid="track-setup-modal">
      <div className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-[#060b18] border border-[#00F0FF33] rounded-t-3xl sm:rounded-3xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[#00F0FF] font-bold text-lg flex items-center gap-2"><Navigation className="w-5 h-5" /> Track {member.name}</h3>
          <button onClick={onClose} data-testid="track-setup-close" className="text-cyan-200/60 hover:text-cyan-100"><X className="w-5 h-5" /></button>
        </div>
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div> : data && (
          <div className="space-y-5">
            <div>
              <p className="text-cyan-100 font-semibold text-sm flex items-center gap-2 mb-1"><Link2 className="w-4 h-4 text-[#00F0FF]" /> 1. Quick live link (any moment)</p>
              <p className="text-cyan-200/50 text-xs mb-2">Send this to {member.name}. When they open it and tap “Allow location”, Nexus sees where they are while the page stays open.</p>
              <div className="flex gap-2">
                <input readOnly value={data.live_url} data-testid="track-live-url" className="flex-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2 text-cyan-50 text-xs" />
                <button onClick={() => copy(data.live_url)} data-testid="track-copy-link" className="px-3 rounded-xl text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11]"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
            <div>
              <p className="text-cyan-100 font-semibold text-sm flex items-center gap-2 mb-1"><Smartphone className="w-4 h-4 text-[#00F0FF]" /> 2. Always-on (iPhone Shortcut)</p>
              <p className="text-cyan-200/50 text-xs mb-2">For continuous background tracking, set up a free Apple Shortcut on {member.name}'s iPhone once:</p>
              <ol className="text-cyan-100/80 text-xs space-y-1.5 list-decimal list-inside bg-[#00F0FF08] border border-[#00F0FF22] rounded-xl p-3">
                <li>Open the <b>Shortcuts</b> app → <b>Automation</b> tab → <b>＋</b> → <b>Create Personal Automation</b>.</li>
                <li>Choose <b>Time of Day</b> (or “Arrive”/“Leave”), set it to repeat (e.g. every hour), and <b>turn off “Ask Before Running.”</b></li>
                <li>Add action <b>Get Current Location</b>, then <b>Get Contents of URL</b>.</li>
                <li>Set URL to the <b>ingest URL</b> below, Method <b>POST</b>, Request Body <b>JSON</b> with:
                  <div className="mt-1 font-mono text-[10px] text-cyan-200/70">token = <span className="text-[#00F0FF]">{data.token}</span>, lat = (Latitude), lng = (Longitude), source = "shortcut"</div>
                </li>
              </ol>
              <div className="flex gap-2 mt-2">
                <input readOnly value={data.ingest_url} data-testid="track-ingest-url" className="flex-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2 text-cyan-50 text-xs" />
                <button onClick={() => copy(data.ingest_url)} className="px-3 rounded-xl text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11]"><Copy className="w-4 h-4" /></button>
              </div>
              <div className="flex gap-2 mt-2">
                <input readOnly value={data.token} className="flex-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2 text-cyan-50 text-xs" />
                <button onClick={() => copy(data.token)} data-testid="track-copy-token" className="px-3 rounded-xl text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11]"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Life360Modal({ userId, onClose, onSynced }) {
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("password"); // password (simple) | token (fallback)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try { setStatus(await life360Status(userId)); } catch (e) {} finally { setLoading(false); }
  }, [userId]);
  useEffect(() => { refresh(); }, [refresh]);

  const connect = async () => {
    const payload = mode === "token"
      ? { access_token: accessToken.trim() }
      : { email: email.trim(), password };
    if (mode === "token" && !payload.access_token) { toast.error("Paste your Life360 access token"); return; }
    if (mode === "password" && (!payload.email || !payload.password)) { toast.error("Enter your Life360 email and password"); return; }
    setBusy(true);
    try {
      const r = await life360Connect(userId, payload);
      toast.success(`Life360 connected — pulled ${r.located} of ${r.members} members`);
      setPassword(""); setAccessToken(""); await refresh(); onSynced && onSynced();
    } catch (e) { toast.error(e?.response?.data?.detail || "Life360 connect failed"); }
    finally { setBusy(false); }
  };
  const syncNow = async () => {
    setBusy(true);
    try { const r = await life360Sync(userId); toast.success(`Synced — ${r.located} of ${r.members} located`); await refresh(); onSynced && onSynced(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await life360Disconnect(userId); toast.success("Life360 disconnected"); await refresh(); }
    catch (e) { toast.error("Could not disconnect"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4" data-testid="life360-modal">
      <div className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-[#060b18] border border-[#00F0FF33] rounded-t-3xl sm:rounded-3xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[#00F0FF] font-bold text-lg flex items-center gap-2"><Satellite className="w-5 h-5" /> Life360 Live Tracking</h3>
          <button onClick={onClose} data-testid="life360-close" className="text-cyan-200/60 hover:text-cyan-100"><X className="w-5 h-5" /></button>
        </div>
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div> : (
          <div className="space-y-4">
            {status?.connected ? (
              <>
                <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-3 flex items-start gap-2" data-testid="life360-connected">
                  <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-emerald-100 text-sm font-semibold truncate">Connected{status.email && status.email !== "token" ? ` as ${status.email}` : ""}</p>
                    <p className="text-emerald-200/60 text-xs">
                      {status.member_count != null ? `${status.member_count} people in your circles` : ""}
                      {status.last_sync ? ` · last sync ${new Date(status.last_sync).toLocaleTimeString()}` : ""}
                    </p>
                    {status.last_error && <p className="text-rose-300 text-xs mt-1">Last issue: {status.last_error}</p>}
                  </div>
                </div>
                <p className="text-cyan-200/60 text-xs">Nexus auto-refreshes everyone's location every 5 minutes in the background. Locations flow straight onto the map above.</p>
                <div className="flex gap-2">
                  <button onClick={syncNow} disabled={busy} data-testid="life360-sync" className="flex-1 py-2.5 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync now
                  </button>
                  <button onClick={disconnect} disabled={busy} data-testid="life360-disconnect" className="px-4 py-2.5 rounded-xl text-sm font-semibold text-rose-200 border border-rose-500/40 hover:bg-rose-500/10 disabled:opacity-50">Disconnect</button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-[#00F0FF08] border border-[#00F0FF22] rounded-xl p-3 space-y-1.5">
                  <p className="text-cyan-100 text-sm font-semibold">One-time family setup</p>
                  <ol className="text-cyan-100/80 text-xs space-y-1 list-decimal list-inside">
                    <li>Everyone installs the free <b>Life360</b> app and joins your Circle.</li>
                    <li>They allow <b>“Always” location</b> once — that's it, forever.</li>
                  </ol>
                </div>

                <div className="flex gap-2">
                  <button onClick={() => setMode("password")} data-testid="life360-mode-password" className={`flex-1 py-2 rounded-xl text-xs font-bold border ${mode === "password" ? "text-[#030712] bg-[#00F0FF] border-[#00F0FF]" : "text-cyan-200/70 border-[#00F0FF33]"}`}>Email + password · easy</button>
                  <button onClick={() => setMode("token")} data-testid="life360-mode-token" className={`flex-1 py-2 rounded-xl text-xs font-bold border ${mode === "token" ? "text-[#030712] bg-[#00F0FF] border-[#00F0FF]" : "text-cyan-200/70 border-[#00F0FF33]"}`}>Access token</button>
                </div>

                {mode === "password" ? (
                  <>
                    <p className="text-cyan-200/60 text-xs">Just type the Life360 email &amp; password you signed up with. That's it.</p>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Life360 email" type="email" data-testid="life360-email" className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 text-sm" />
                    <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Life360 password" type="password" data-testid="life360-password" onKeyDown={(e) => e.key === "Enter" && connect()} className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 text-sm" />
                  </>
                ) : (
                  <>
                    <div className="bg-[#0a1020] border border-[#00F0FF22] rounded-xl p-3">
                      <p className="text-cyan-100 text-xs font-semibold mb-1">Only if password doesn't work. Get token on a computer:</p>
                      <ol className="text-cyan-100/75 text-[11px] space-y-1 list-decimal list-inside">
                        <li>Go to <b>life360.com/login</b> and sign in.</li>
                        <li>Press <b>F12</b> → open the <b>Network</b> tab.</li>
                        <li>Log in; find the request named <b>token</b>. In its <b>Response</b>, copy the long <b>access_token</b> value.</li>
                        <li>Paste it below.</li>
                      </ol>
                    </div>
                    <textarea value={accessToken} onChange={(e) => setAccessToken(e.target.value)} placeholder="Paste Life360 access_token here" data-testid="life360-token" rows={3} className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 text-xs font-mono break-all" />
                  </>
                )}

                <button onClick={connect} disabled={busy} data-testid="life360-connect" className="w-full py-2.5 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Satellite className="w-4 h-4" />} Connect Life360
                </button>
                <p className="text-cyan-200/40 text-[11px]">Your login/token is encrypted (AES-256) and used only to read your circle's location. Nexus never posts anything to Life360.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function GmapsModal({ userId, onClose, onSynced }) {
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [cookies, setCookies] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try { setStatus(await gmapsStatus(userId)); } catch (e) {} finally { setLoading(false); }
  }, [userId]);
  useEffect(() => { refresh(); }, [refresh]);

  const connect = async () => {
    if (!email.trim() || !cookies.trim()) { toast.error("Enter the receiver Gmail and paste the cookies file"); return; }
    setBusy(true);
    try {
      const r = await gmapsConnect(userId, email.trim(), cookies);
      toast.success(`Google Maps connected — ${r.located} of ${r.members} people located`);
      setCookies(""); await refresh(); onSynced && onSynced();
    } catch (e) { toast.error(e?.response?.data?.detail || "Google Maps connect failed"); }
    finally { setBusy(false); }
  };
  const syncNow = async () => {
    setBusy(true);
    try { const r = await gmapsSync(userId); toast.success(`Synced — ${r.located} of ${r.members} located`); await refresh(); onSynced && onSynced(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await gmapsDisconnect(userId); toast.success("Google Maps disconnected"); await refresh(); }
    catch (e) { toast.error("Could not disconnect"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4" data-testid="gmaps-modal">
      <div className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-[#060b18] border border-[#00F0FF33] rounded-t-3xl sm:rounded-3xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[#00F0FF] font-bold text-lg flex items-center gap-2"><MapPin className="w-5 h-5" /> Google Maps Live Tracking</h3>
          <button onClick={onClose} data-testid="gmaps-close" className="text-cyan-200/60 hover:text-cyan-100"><X className="w-5 h-5" /></button>
        </div>
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div> : (
          <div className="space-y-4">
            {status?.connected ? (
              <>
                <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-3 flex items-start gap-2" data-testid="gmaps-connected">
                  <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-emerald-100 text-sm font-semibold truncate">Connected · receiver {status.email}</p>
                    <p className="text-emerald-200/60 text-xs">
                      {status.member_count != null ? `${status.member_count} people sharing` : ""}
                      {status.last_sync ? ` · last sync ${new Date(status.last_sync).toLocaleTimeString()}` : ""}
                    </p>
                    {status.last_error && <p className="text-rose-300 text-xs mt-1">Last issue: {status.last_error}</p>}
                  </div>
                </div>
                <p className="text-cyan-200/60 text-xs">Nexus refreshes everyone's location every 5 minutes in the background. If it says cookies expired, re-export and reconnect.</p>
                <div className="flex gap-2">
                  <button onClick={syncNow} disabled={busy} data-testid="gmaps-sync" className="flex-1 py-2.5 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync now
                  </button>
                  <button onClick={disconnect} disabled={busy} data-testid="gmaps-disconnect" className="px-4 py-2.5 rounded-xl text-sm font-semibold text-rose-200 border border-rose-500/40 hover:bg-rose-500/10 disabled:opacity-50">Disconnect</button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-[#00F0FF08] border border-[#00F0FF22] rounded-xl p-3 space-y-1.5">
                  <p className="text-cyan-100 text-sm font-semibold">How this works</p>
                  <ol className="text-cyan-100/80 text-xs space-y-1 list-decimal list-inside">
                    <li>Family shares their location (Google Maps → Location sharing → “Until you turn this off”) with your <b>receiver Gmail</b>.</li>
                    <li>On a computer, sign into that Gmail, export its cookies with a browser extension, and paste them below.</li>
                    <li>Nexus reads the shared locations and drops them on your map — background, phone-locked.</li>
                  </ol>
                </div>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Receiver Gmail (e.g. nexustrack@gmail.com)" type="email" data-testid="gmaps-email" className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 text-sm" />
                <textarea value={cookies} onChange={(e) => setCookies(e.target.value)} placeholder="Paste the exported cookies.txt contents here" data-testid="gmaps-cookies" rows={4} className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 text-xs font-mono break-all" />
                <button onClick={connect} disabled={busy} data-testid="gmaps-connect" className="w-full py-2.5 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} Connect Google Maps
                </button>
                <p className="text-cyan-200/40 text-[11px]">Your cookies are encrypted (AES-256) and used only to read shared locations. Nexus never posts anything to Google.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function NexusTrack({ session }) {  const userId = session.user_id;
  const [members, setMembers] = useState([]);
  const [weather, setWeather] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // member id for trail
  const [trail, setTrail] = useState([]);
  const [setupFor, setSetupFor] = useState(null);
  const [showLife360, setShowLife360] = useState(false);
  const [showGmaps, setShowGmaps] = useState(false);
  const [l360Connected, setL360Connected] = useState(false);
  const [gmapsConnected, setGmapsConnected] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [r, w] = await Promise.all([trackLatest(userId), trackWeather(userId).catch(() => ({ members: [] }))]);
      setMembers(r.members || []);
      const wm = {}; (w.members || []).forEach((m) => { wm[m.id] = m; });
      setWeather(wm);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { life360Status(userId).then((s) => setL360Connected(!!s.connected)).catch(() => {}); gmapsStatus(userId).then((s) => setGmapsConnected(!!s.connected)).catch(() => {}); }, [userId]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 30000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const showTrail = async (m) => {
    setSelected(m.id);
    try {
      const r = await trackHistory(userId, m.id, 24);
      setTrail((r.points || []).map((p) => [p.lat, p.lng]));
      if (!r.points?.length) toast.info(`No location history yet for ${m.name}`);
    } catch (e) { toast.error("Could not load trail"); }
  };
  const clearTrail = () => { setSelected(null); setTrail([]); };

  const located = members.filter((m) => m.lat != null && m.lng != null);
  const allPts = located.map((m) => [m.lat, m.lng]);
  const center = allPts[0] || [41.8781, -87.6298]; // default Chicago
  const dangers = members.filter((m) => (weather[m.id]?.danger || []).length);

  return (
    <div className="h-full flex flex-col bg-[#030712] nx-display" data-testid="nexus-track-panel">
      <div className="px-4 pt-4 pb-2 shrink-0">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-[#00F0FF] font-bold text-xl flex items-center gap-2"><MapPin className="w-6 h-6" /> Family Map</h2>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setShowGmaps(true)} data-testid="gmaps-open" className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 border ${gmapsConnected ? "text-emerald-200 border-emerald-500/40 bg-emerald-500/10" : "text-[#00F0FF] border-[#00F0FF44] hover:bg-[#00F0FF11]"}`}>
              <MapPin className="w-3.5 h-3.5" /> {gmapsConnected ? "Google · Live" : "Google Maps"}
            </button>
            <button onClick={() => setShowLife360(true)} data-testid="life360-open" className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 border ${l360Connected ? "text-emerald-200 border-emerald-500/40 bg-emerald-500/10" : "text-[#00F0FF] border-[#00F0FF44] hover:bg-[#00F0FF11]"}`}>
              <Satellite className="w-3.5 h-3.5" /> {l360Connected ? "Life360 · Live" : "Life360"}
            </button>
          </div>
        </div>
        {dangers.length > 0 && (
          <div className="mt-2 bg-rose-500/10 border border-rose-500/40 rounded-xl px-3 py-2 flex items-start gap-2" data-testid="track-danger">
            <AlertTriangle className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
            <p className="text-rose-100 text-xs">{dangers.map((m) => `${m.name}: ${weather[m.id].danger.join(", ")}`).join(" · ")}</p>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 relative">
        {loading ? (
          <div className="h-full flex items-center justify-center text-cyan-200/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%", background: "#0a1020" }} data-testid="track-map">
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {allPts.length > 0 && !selected && <FitBounds points={allPts} />}
            {selected && trail.length > 0 && <FitBounds points={trail} />}
            {located.map((m, i) => (
              <Marker key={m.id} position={[m.lat, m.lng]} icon={pinIcon(COLORS[i % COLORS.length], (m.name || "?")[0])}>
                <Popup><b>{m.name}</b><br />{m.relation}<br />{timeAgo(m.mins_ago)}</Popup>
              </Marker>
            ))}
            {selected && trail.length > 1 && <Polyline positions={trail} pathOptions={{ color: "#00F0FF", weight: 4, opacity: 0.7 }} />}
          </MapContainer>
        )}
      </div>

      <div className="shrink-0 max-h-[38%] overflow-y-auto border-t border-[#00F0FF22] p-3 space-y-2">
        {selected && (
          <button onClick={clearTrail} data-testid="track-clear-trail" className="text-xs text-[#00F0FF] flex items-center gap-1 mb-1"><X className="w-3 h-3" /> Clear trail, show everyone</button>
        )}
        {members.map((m, i) => {
          const w = weather[m.id];
          return (
            <div key={m.id} data-testid={`track-member-${m.id}`} className="bg-[#060b18] border border-[#00F0FF22] rounded-xl p-3 flex items-center gap-3">
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-[#030712] shrink-0" style={{ background: COLORS[i % COLORS.length] }}>{(m.name || "?")[0]}</span>
              <div className="min-w-0 flex-1">
                <p className="text-cyan-50 font-semibold text-sm truncate">{m.name} <span className="text-cyan-200/40 font-normal">· {m.relation}</span></p>
                <p className="text-cyan-200/50 text-[11px] flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> {m.lat != null ? timeAgo(m.mins_ago) : (m.tracking ? "waiting for first ping" : "not sharing yet")}
                  {w && w.temp_c != null ? ` · ${Math.round(w.temp_c)}°C` : ""}
                  {w && (w.danger || []).length ? <span className="text-rose-300 font-bold">· {w.danger.join(", ")}</span> : ""}
                </p>
              </div>
              {m.lat != null && (
                <button onClick={() => showTrail(m)} data-testid={`track-trail-${m.id}`} className="p-1.5 rounded-lg text-cyan-200/60 hover:text-cyan-100 hover:bg-[#00F0FF11]" title="Show today's trail"><Route className="w-4 h-4" /></button>
              )}
              <button onClick={() => setSetupFor(m)} data-testid={`track-setup-${m.id}`} className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] shrink-0">
                {m.tracking ? "Link" : "Set up"}
              </button>
            </div>
          );
        })}
        {members.length === 0 && <p className="text-cyan-200/40 text-sm text-center py-4">Add family members first (FAMILY tab).</p>}
      </div>

      {setupFor && <SetupModal member={setupFor} userId={userId} onClose={() => { setSetupFor(null); load(); }} />}
      {showLife360 && <Life360Modal userId={userId} onClose={() => setShowLife360(false)} onSynced={() => { setL360Connected(true); load(); }} />}
      {showGmaps && <GmapsModal userId={userId} onClose={() => setShowGmaps(false)} onSynced={() => { setGmapsConnected(true); load(); }} />}
    </div>
  );
}
